import React, { createContext, useContext, useEffect, useState, useMemo } from 'react'
import { useListContext } from 'react-admin'
import { getDocumentCounts, DocumentCount } from 'api/gatedCommunity'

interface DocumentCountContextValue {
  counts: Map<string, number>
  loading: boolean
  error: boolean
}

const DocumentCountContext = createContext<DocumentCountContextValue>({
  counts: new Map(),
  loading: true,
  error: false
})

export const useDocumentCounts = () => useContext(DocumentCountContext)

export const DocumentCountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const listContext = useListContext()
  const [counts, setCounts] = useState<Map<string, number>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  // Create a stable key from the community IDs to prevent unnecessary re-fetches
  const communityIdsKey = useMemo(() => {
    if (!listContext.data || listContext.data.length === 0) return ''
    return listContext.data.map((record: any) => record.id).sort().join(',')
  }, [listContext.data])

  useEffect(() => {
    if (!communityIdsKey) {
      setLoading(false)
      return
    }

    const fetchCounts = async () => {
      setLoading(true)
      try {
        const communityIds = communityIdsKey.split(',')
        const results = await getDocumentCounts(communityIds)
        
        const countsMap = new Map<string, number>()
        results.forEach((result: DocumentCount) => {
          countsMap.set(result.community_id, result.total)
        })
        
        setCounts(countsMap)
        setError(false)
      } catch (err) {
        console.error('Failed to fetch document counts:', err)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    fetchCounts()
  }, [communityIdsKey])

  const value = useMemo(() => ({ counts, loading, error }), [counts, loading, error])

  return (
    <DocumentCountContext.Provider value={value}>
      {children}
    </DocumentCountContext.Provider>
  )
}
